import type { GetColorDefinition } from 'atoms/colors/colors';

import Container from 'quarks/Container';
import Flex from 'quarks/Flex';
import Grid from 'quarks/Grid';
import Image from 'quarks/Image';
import Text from 'quarks/Text';
import type { FlexProps } from 'quarks/interpolations/flex';

import { objectEntries } from 'utils/typeUtils';

import type { ContentfulTemplateCustomerStory } from 'graphqlTypes';
import type { FC } from 'react';

interface CustomerDetailHeadingProps
  extends FlexProps,
    Pick<
      ContentfulTemplateCustomerStory,
      'company' | 'growingWithUs' | 'companySize' | 'industry' | 'companyLocation'
    > {
  borderColor: GetColorDefinition;
}

const CustomerDetailHeadingCard: FC<CustomerDetailHeadingProps> = ({
  company,
  growingWithUs,
  companySize,
  industry,
  companyLocation,
  borderColor,
  ...props
}) => {
  if (!company) {
    return null;
  }

  const stats = {
    'growing with us': growingWithUs,
    'company size': companySize,
    industry,
    location: companyLocation,
  };

  return (
    <Flex
      flexDirection="column"
      borderTop="16px solid"
      borderColor={borderColor}
      borderRadius="6px"
      backgroundColor="common-white"
      boxShadow="0 16px 32px rgb(75 77 82 / 24%)"
      paddingAll={32}
      md={{
        paddingX: 64,
      }}
      xl={{
        borderTop: 'none',
        borderLeft: '16px solid',
        borderColor,
        boxShadow: 'none',
      }}
      {...props}
    >
      {company.onLightLogo && (
        // Hardcode height on both container and image to prevent inconsistencies between SVG and webp logos
        <Container height="50px" marginBottom={32}>
          <Image
            image={company.onLightLogo}
            alt={company.name ? `${company.name}-logo` : ''}
            height="50px"
            objectFit="contain"
            maxWidth="100%"
            css="& img { width: auto; }"
          />
        </Container>
      )}
      <Grid
        gridTemplateColumns="1fr"
        md={{
          gridTemplateColumns: 'repeat(2, 1fr)',
        }}
        gap="24px"
      >
        {objectEntries(stats).map(
          ([key, value]) =>
            value && (
              <Flex flexDirection="column" key={key}>
                <Text
                  textTransform="uppercase"
                  fontWeight="bold"
                  fontSize="textSm"
                  xl={{
                    fontSize: 'textMd',
                  }}
                >
                  {key}
                </Text>
                <Text
                  fontSize="textMd"
                  xl={{
                    fontSize: 'textXl',
                  }}
                >
                  {value}
                </Text>
              </Flex>
            ),
        )}
      </Grid>
    </Flex>
  );
};

export default CustomerDetailHeadingCard;
